import { ACTION_IDS, type ActionId } from "./input/actions.ts";

/** Map camera actions that stay live in View mode. */
export const VIEW_MODE_ACTIONS = [
  "panView",
  "rotateView",
  "zoomMap",
  "zoomIn",
  "zoomOut",
  "zoomInMore",
  "zoomOutMore",
  "panLeft",
  "panRight",
  "panUp",
  "panDown",
  "northUp",
  "toggleDrawers",
  "toggleBasemap",
  "toggleStreetView",
] as const satisfies readonly ActionId[];

const VIEW_MODE_ACTION_SET = new Set<string>(VIEW_MODE_ACTIONS);

export type SessionMode = "view" | "edit";
export type LeaveChoice = "save" | "discard" | "cancel";

export interface EditorSession {
  mode: SessionMode;
  dirty: boolean;
  /** Three-way leave prompt: save, discard, or stay in Edit. */
  leavePrompt: boolean;
}

export function createEditorSession(): EditorSession {
  return { mode: "view", dirty: false, leavePrompt: false };
}

/** Opening a piece always starts in View, with a clean saved copy. */
export function openPieceSession(): EditorSession {
  return { mode: "view", dirty: false, leavePrompt: false };
}

export function enterEdit(session: EditorSession): EditorSession {
  if (session.mode === "edit") return { ...session, leavePrompt: false };
  return { mode: "edit", dirty: false, leavePrompt: false };
}

export function markSessionDirty(session: EditorSession): EditorSession {
  if (session.mode !== "edit") return session;
  if (session.dirty) return session;
  return { ...session, dirty: true };
}

export function markSessionSaved(session: EditorSession): EditorSession {
  if (!session.dirty && !session.leavePrompt) return session;
  return { ...session, dirty: false, leavePrompt: false };
}

/**
 * Ask to leave Edit (exit button, another piece, or leaving the page).
 * A clean session returns to View. A dirty session opens the save prompt.
 */
export function requestLeaveEdit(session: EditorSession): EditorSession {
  if (session.mode !== "edit") return { ...session, leavePrompt: false };
  if (!session.dirty) return { mode: "view", dirty: false, leavePrompt: false };
  return { ...session, leavePrompt: true };
}

export function resolveLeaveEdit(session: EditorSession, choice: LeaveChoice): EditorSession {
  if (!session.leavePrompt) return session;
  if (choice === "cancel") return { ...session, leavePrompt: false };
  return { mode: "view", dirty: false, leavePrompt: false };
}

export function leaveNeedsSave(session: EditorSession): boolean {
  return session.leavePrompt && session.dirty;
}

export function editingActive(session: EditorSession): boolean {
  return session.mode === "edit" && !session.leavePrompt;
}

export function isViewModeAction(action: ActionId): boolean {
  return VIEW_MODE_ACTION_SET.has(action);
}

/**
 * Editing shortcuts (select, draw, save, style, …) are inert in View mode.
 * `enterEdit` is the only edit-related shortcut accepted in View.
 * `exitEdit` is Edit-only.
 */
export function actionAllowedInSession(session: EditorSession, action: ActionId): boolean {
  if (action === "enterEdit") return session.mode === "view" && !session.leavePrompt;
  if (action === "exitEdit") return editingActive(session);
  if (editingActive(session)) return true;
  return isViewModeAction(action);
}

export function editingActionIds(): ActionId[] {
  return ACTION_IDS.filter((id) => !VIEW_MODE_ACTION_SET.has(id));
}
